import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { type FindingSeverity } from "@/lib/api/building";
import {
  resolvedEquipmentZoneIds,
  type EquipmentCard,
  type Zone,
} from "@/lib/building-domain";
import { chipsForOptionGroup } from "@/lib/variant-option-chips";
import {
  equipmentTypeIcon,
  equipmentTypeNameFi,
  useEquipmentTypeMeta,
  useKbSensorTypes,
} from "@/lib/equipment-type-meta";
import { equipmentTypeAccent } from "@/lib/equipment-type-accent";
import {
  getKBEquipmentType,
  type KBComponent,
  type KBConfigOptionGroup,
  type KBStream,
} from "@/lib/api/agent";
import {
  activeComponentsByStream,
  deriveActiveComponentIds,
  moveComponentInStream,
  orderedActiveComponents,
} from "@/lib/active-components";
import { cn } from "@/lib/utils";
import {
  inputGlowWrap,
  inputGlowInner,
  cardDestructive,
  moduleCard,
  cardToneDot,
  cardTonePill,
} from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";
import { AccentRailDisclosure } from "@/components/ui/accent-rail-disclosure";
import { SimulatedConnectorBadge } from "./SimulatedConnectorCardControls";
import { SimulatedDriverSection } from "./SimulatedDriverSection";
import { useSimulatedConnector } from "./use-simulated-connector";
import { EquipmentTypeSelect } from "./EquipmentTypeSelect";
import {
  dirty_actions_visible,
  discard_equipment_draft,
  equipment_draft_from_card,
  is_equipment_draft_dirty,
  type EquipmentStructureDraft,
} from "./equipment-row-chrome";

// ── EquipmentTile ─────────────────────────────────────────────────────────────

interface TileProps {
  buildingId: string;
  equipment: EquipmentCard;
  zones: Zone[];
  locations: string[];
  operator: boolean;
  configurator: boolean;
  max_severity?: FindingSeverity;
  on_update: (
    id: string,
    data: {
      name?: string;
      equipment_type?: string;
      equipment_tag?: string;
      location?: string | null;
      declared_variants?: string[];
      declared_component_order?: string[];
      declared_component_subtypes?: Record<string, string>;
      declared_stream_component_order?: Record<string, string[]>;
    },
  ) => Promise<void>;
  on_delete: (id: string) => Promise<void>;
  // Zone links go through the dedicated endpoints (configurator-only), never the update bypass.
  on_add_zone_link: (equipmentId: string, zoneId: string) => Promise<void>;
  on_remove_zone_link: (equipmentId: string, zoneId: string) => Promise<void>;
  open: boolean;
  on_toggle: () => void;
}

export function EquipmentTile({
  buildingId,
  equipment,
  zones,
  locations,
  operator,
  configurator,
  max_severity,
  on_update,
  on_delete,
  on_add_zone_link,
  on_remove_zone_link,
  open,
  on_toggle,
}: TileProps) {
  const eqMeta = useEquipmentTypeMeta();
  const sensors = useKbSensorTypes();
  const [confirm_delete, set_confirm_delete] = useState(false);
  const [name, set_name] = useState(equipment.name);
  const [equipment_type, set_equipment_type] = useState(
    equipment.equipment_type,
  );
  const [tag, set_tag] = useState(equipment.equipment_tag);
  const [location, set_location] = useState<string>(equipment.location ?? "");
  const [zone_link_busy, set_zone_link_busy] = useState(false);
  const [zone_link_error, set_zone_link_error] = useState<string | null>(null);
  const [declared_variants, set_declared_variants] = useState<string[]>(
    equipment.declared_variants ?? [],
  );
  const [option_groups, set_option_groups] = useState<KBConfigOptionGroup[]>(
    [],
  );
  const [kb_components, set_kb_components] = useState<KBComponent[]>([]);
  const [kb_streams, set_kb_streams] = useState<KBStream[]>([]);
  const [component_order, set_component_order] = useState<string[]>(
    equipment.declared_component_order ?? [],
  );
  const [stream_component_order, set_stream_component_order] = useState<
    Record<string, string[]>
  >(equipment.declared_stream_component_order ?? {});
  const [component_subtypes, set_component_subtypes] = useState<
    Record<string, string>
  >(equipment.declared_component_subtypes ?? {});
  const [submitting, set_submitting] = useState(false);
  const sim = useSimulatedConnector(buildingId, equipment.id, operator, open);

  function apply_draft(next: EquipmentStructureDraft) {
    set_name(next.name);
    set_equipment_type(next.equipment_type);
    set_tag(next.equipment_tag);
    set_location(next.location);
    set_declared_variants(next.declared_variants);
    set_component_order(next.declared_component_order);
    set_stream_component_order(next.declared_stream_component_order);
    set_component_subtypes(next.declared_component_subtypes);
  }

  const saved_draft = equipment_draft_from_card(equipment);
  const current_draft: EquipmentStructureDraft = {
    name,
    equipment_type,
    equipment_tag: tag,
    location,
    declared_variants,
    declared_component_order: component_order,
    declared_component_subtypes: component_subtypes,
    declared_stream_component_order: stream_component_order,
  };
  const dirty = is_equipment_draft_dirty(current_draft, saved_draft);

  // Variant option groups (which gate applicable capabilities) + components (Rakenne's
  // active-component list, order and subtype) — loaded while the row is expanded; kept
  // when collapsed so a dirty draft can re-open without a flash.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getKBEquipmentType(equipment_type)
      .then((d) => {
        if (cancelled) return;
        set_option_groups(d.config_option_groups ?? []);
        set_kb_components(d.components ?? []);
        set_kb_streams(d.streams ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        set_option_groups([]);
        set_kb_components([]);
        set_kb_streams([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, equipment_type]);

  // Rakenne (#682): the derived active-component set updates live as declared_variants is
  // toggled in this same form — presence stays the variant's lever (Kyvykkyydet is the
  // canonical editable home; the toggle here is the pre-existing dual-home, out of scope to
  // fix), order/subtype are edited here.
  const active_component_ids = useMemo(
    () =>
      deriveActiveComponentIds(kb_components, option_groups, declared_variants),
    [kb_components, option_groups, declared_variants],
  );
  const ordered_active_components = useMemo(
    () =>
      orderedActiveComponents(
        kb_components,
        active_component_ids,
        component_order,
      ),
    [kb_components, active_component_ids, component_order],
  );
  // Rakenne grouped-by-stream (#1157): when the type declares `streams[]`, group active
  // components by duct/pipe instead of one flat list — the type's own catalog is the only
  // stream source, never a hardcoded frontend order.
  const stream_groups = useMemo(
    () =>
      activeComponentsByStream(
        kb_components,
        active_component_ids,
        kb_streams,
        stream_component_order,
      ),
    [kb_components, active_component_ids, kb_streams, stream_component_order],
  );
  const inactive_components = kb_components.filter(
    (c) => !active_component_ids.has(c.id),
  );

  function move_component(component_id: string, direction: -1 | 1) {
    const ids = ordered_active_components.map((c) => c.id);
    const idx = ids.indexOf(component_id);
    const swap_with = idx + direction;
    if (idx < 0 || swap_with < 0 || swap_with >= ids.length) return;
    [ids[idx], ids[swap_with]] = [ids[swap_with], ids[idx]];
    set_component_order(ids);
  }

  function move_component_in_stream(
    stream_id: string,
    display_order_ids: string[],
    component_id: string,
    direction: -1 | 1,
  ) {
    set_stream_component_order((prev) =>
      moveComponentInStream(
        prev,
        stream_id,
        display_order_ids,
        component_id,
        direction,
      ),
    );
  }

  function set_subtype(component_id: string, subtype_id: string) {
    set_component_subtypes((prev) => {
      if (!subtype_id) {
        return Object.fromEntries(
          Object.entries(prev).filter(([id]) => id !== component_id),
        );
      }
      return { ...prev, [component_id]: subtype_id };
    });
  }

  function toggle_variant(group: KBConfigOptionGroup, choice_id: string) {
    set_declared_variants((prev) => {
      if (group.type === "boolean") {
        return prev.includes(choice_id)
          ? prev.filter((v) => v !== choice_id)
          : [...prev, choice_id];
      }
      const siblings = new Set(group.choices.map((c) => c.id));
      const without = prev.filter((v) => !siblings.has(v));
      return prev.includes(choice_id) ? without : [...without, choice_id];
    });
  }

  const linked_zone_ids = resolvedEquipmentZoneIds(equipment);
  const unlinked_zones = zones.filter((z) => !linked_zone_ids.includes(z.id));

  async function add_zone_link(zoneId: string) {
    if (!zoneId) return;
    set_zone_link_busy(true);
    set_zone_link_error(null);
    try {
      await on_add_zone_link(equipment.id, zoneId);
    } catch {
      set_zone_link_error("Vyöhykelinkin lisääminen epäonnistui.");
    } finally {
      set_zone_link_busy(false);
    }
  }

  async function remove_zone_link(zoneId: string) {
    set_zone_link_busy(true);
    set_zone_link_error(null);
    try {
      await on_remove_zone_link(equipment.id, zoneId);
    } catch {
      set_zone_link_error("Vyöhykelinkin poisto epäonnistui.");
    } finally {
      set_zone_link_busy(false);
    }
  }

  async function save_draft() {
    set_submitting(true);
    try {
      const next_order =
        kb_components.length > 0
          ? ordered_active_components.map((c) => c.id)
          : component_order;
      await on_update(equipment.id, {
        name: name.trim(),
        equipment_type,
        equipment_tag: tag.trim(),
        location: location || null,
        declared_variants,
        declared_component_order: next_order,
        declared_component_subtypes: component_subtypes,
        declared_stream_component_order: stream_component_order,
      });
      apply_draft({
        ...current_draft,
        name: name.trim(),
        equipment_tag: tag.trim(),
        declared_component_order: next_order,
      });
    } finally {
      set_submitting(false);
    }
  }

  const accent = equipmentTypeAccent(equipment_type);
  const show_dirty_actions = dirty_actions_visible(dirty);

  if (confirm_delete) {
    return (
      <div className={cn(cardDestructive, "flex flex-col gap-2 p-4 text-xs")}>
        <p className="font-medium">
          Poistetaanko &ldquo;{equipment.name}&rdquo;?
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => void on_delete(equipment.id)}
            className="rounded bg-destructive px-2 py-1 text-destructive-foreground hover:bg-destructive/80"
          >
            Poista
          </button>
          <button
            onClick={() => set_confirm_delete(false)}
            className="rounded px-2 py-1 text-muted-foreground hover:text-foreground"
          >
            Peruuta
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative">
      <AccentRailDisclosure
        open={open}
        onToggle={on_toggle}
        kind="group"
        accentColor={accent}
        triggerClassName="items-start gap-2.5 px-3 py-2.5 hover:bg-surface/60"
        chevronClassName="h-4 w-4"
        actions={
          show_dirty_actions ? (
            <div
              className={cn(
                "flex shrink-0 items-center gap-1.5 py-2 pr-2",
                operator && "pr-8",
              )}
            >
              <button
                type="button"
                disabled={submitting || !name.trim()}
                onClick={() => void save_draft()}
                className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
              >
                {submitting ? "Tallennetaan..." : "Tallenna"}
              </button>
              <button
                type="button"
                onClick={() =>
                  apply_draft(discard_equipment_draft(saved_draft))
                }
                className="rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Peruuta
              </button>
            </div>
          ) : undefined
        }
        trigger={
          <>
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-lg leading-none"
              style={{
                backgroundColor: `color-mix(in oklab, ${accent} 18%, transparent)`,
                boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${accent} 45%, transparent)`,
              }}
              aria-hidden
            >
              {equipmentTypeIcon(eqMeta, equipment_type, sensors)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{name}</div>
              <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                <span className="font-medium">Tyyppi</span>
                {" · "}
                {equipmentTypeNameFi(eqMeta, equipment_type, sensors)}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                <span className="font-medium">Palvelee vyöhykkeitä</span>
                {linked_zone_ids.length === 0 ? (
                  <span>· —</span>
                ) : (
                  linked_zone_ids.map((zid) => {
                    const z = zones.find((zz) => zz.id === zid);
                    return (
                      <span
                        key={zid}
                        className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary"
                      >
                        {z?.name ?? zid}
                      </span>
                    );
                  })
                )}
              </div>
            </div>
            {tag && (
              <span className="shrink-0 font-mono text-xs text-muted-foreground">
                {tag}
              </span>
            )}
            {sim.has_connector && <SimulatedConnectorBadge visible />}
            {max_severity && (
              <span
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  max_severity === "critical"
                    ? "bg-destructive"
                    : max_severity === "warning"
                      ? "bg-warning"
                      : "bg-primary/40",
                )}
                title={
                  max_severity === "critical"
                    ? "Kriittisiä havaintoja"
                    : max_severity === "warning"
                      ? "Varoituksia"
                      : "Huomioita"
                }
              />
            )}
          </>
        }
      >
        {open && (
          <div className="space-y-3 border-t border-border/40 px-3 py-2.5 text-sm">
            {operator && (
              <SimulatedDriverSection
                has_connector={sim.has_connector}
                loading={sim.loading}
                running={sim.status?.running ?? false}
                busy={sim.busy}
                error={sim.error}
                signal_schema={sim.status?.signal_schema}
                unmapped_signals={sim.status?.unmapped_signals}
                on_enable_csv={(file) => sim.enable_with_csv(file)}
                on_disable={() => sim.disable()}
                on_replace_csv={(file) => sim.replace_csv(file)}
              />
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Nimi
                </label>
                <div className={inputGlowWrap}>
                  <div className={inputGlowInner}>
                    <input
                      autoFocus
                      value={name}
                      onChange={(e) => set_name(e.target.value)}
                      placeholder="esim. TK-01"
                      className="w-full bg-transparent px-2 py-1.5 text-sm placeholder:text-muted-foreground outline-none"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Tunniste
                </label>
                <div className={inputGlowWrap}>
                  <div className={inputGlowInner}>
                    <input
                      value={tag}
                      onChange={(e) => set_tag(e.target.value)}
                      placeholder="esim. TK01"
                      className="w-full bg-transparent px-2 py-1.5 text-sm placeholder:text-muted-foreground outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs text-muted-foreground">
                Tyyppi
              </label>
              <EquipmentTypeSelect
                eqMeta={eqMeta}
                value={equipment_type}
                onChange={set_equipment_type}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-muted-foreground">
                Sijainti
              </label>
              {locations.length > 0 ? (
                <select
                  value={location}
                  onChange={(e) => set_location(e.target.value)}
                  className="w-full rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60"
                >
                  <option value="">—</option>
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Ei sijainteja. Lisää ne Sijainnit-osiossa.
                </p>
              )}
            </div>

            {zones.length > 0 && (
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Palvelee vyöhykkeitä
                </label>
                {configurator ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {linked_zone_ids.length === 0 && (
                        <span className="text-xs text-muted-foreground">
                          Ei vyöhykelinkkejä.
                        </span>
                      )}
                      {linked_zone_ids.map((zid) => {
                        const z = zones.find((zz) => zz.id === zid);
                        return (
                          <span
                            key={zid}
                            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary"
                          >
                            {z?.name ?? zid}
                            <button
                              type="button"
                              disabled={zone_link_busy}
                              onClick={() => void remove_zone_link(zid)}
                              title="Poista vyöhykelinkki"
                              className="text-primary/70 hover:text-primary disabled:opacity-50"
                            >
                              ✕
                            </button>
                          </span>
                        );
                      })}
                    </div>
                    {unlinked_zones.length > 0 && (
                      <select
                        value=""
                        disabled={zone_link_busy}
                        onChange={(e) => void add_zone_link(e.target.value)}
                        className="w-full rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60 disabled:opacity-50"
                      >
                        <option value="">+ Lisää vyöhykelinkki…</option>
                        {unlinked_zones.map((z) => (
                          <option key={z.id} value={z.id}>
                            {z.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {zone_link_error && (
                      <p className="text-xs text-destructive">
                        {zone_link_error}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {linked_zone_ids.length === 0 ? (
                      <span className="text-xs text-muted-foreground">
                        Ei vyöhykelinkkejä.
                      </span>
                    ) : (
                      linked_zone_ids.map((zid) => {
                        const z = zones.find((zz) => zz.id === zid);
                        return (
                          <span
                            key={zid}
                            className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary"
                          >
                            {z?.name ?? zid}
                          </span>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {option_groups.length > 0 && (
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Variantit
                </label>
                <div className="space-y-2">
                  {option_groups.map((group) => (
                    <div
                      key={group.id}
                      className="flex flex-wrap items-center gap-2"
                    >
                      <span className="min-w-[110px] text-xs text-muted-foreground">
                        {group.name_fi}
                      </span>
                      <div className="inline-flex flex-wrap gap-1.5">
                        {chipsForOptionGroup(group).map((choice) => {
                          const active = declared_variants.includes(choice.id);
                          return (
                            <button
                              key={choice.id}
                              type="button"
                              aria-pressed={active}
                              onClick={() => toggle_variant(group, choice.id)}
                              className={cn(
                                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                                active
                                  ? "border-primary/40 bg-primary/10 text-primary"
                                  : "border-border/40 text-muted-foreground hover:text-foreground",
                              )}
                            >
                              {choice.name_fi}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {kb_components.length > 0 && (
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Rakenne — aktiiviset komponentit
                </label>
                <div className="space-y-1.5">
                  {kb_streams.length > 0
                    ? stream_groups.map((group) => {
                        const ids = group.components.map((c) => c.id);
                        return (
                          <div key={group.streamId} className="space-y-1">
                            <p className="pt-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70">
                              {group.nameFi}
                            </p>
                            {group.components.map((component, idx) => (
                              <ActiveComponentRow
                                key={`${group.streamId}:${component.id}`}
                                component={component}
                                configurator={configurator}
                                component_subtypes={component_subtypes}
                                set_subtype={set_subtype}
                                can_move_up={idx > 0}
                                can_move_down={idx < ids.length - 1}
                                on_move_up={() =>
                                  move_component_in_stream(
                                    group.streamId,
                                    ids,
                                    component.id,
                                    -1,
                                  )
                                }
                                on_move_down={() =>
                                  move_component_in_stream(
                                    group.streamId,
                                    ids,
                                    component.id,
                                    1,
                                  )
                                }
                              />
                            ))}
                          </div>
                        );
                      })
                    : ordered_active_components.map((component, idx) => (
                        <ActiveComponentRow
                          key={component.id}
                          component={component}
                          configurator={configurator}
                          component_subtypes={component_subtypes}
                          set_subtype={set_subtype}
                          can_move_up={idx > 0}
                          can_move_down={
                            idx < ordered_active_components.length - 1
                          }
                          on_move_up={() => move_component(component.id, -1)}
                          on_move_down={() => move_component(component.id, 1)}
                        />
                      ))}
                  {inactive_components.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      <span
                        className={cn(
                          "h-2 w-2 shrink-0 rounded-full",
                          cardToneDot.empty,
                        )}
                        aria-hidden
                      />
                      <span className="text-[10.5px] text-muted-foreground">
                        Ei käytössä:{" "}
                        {inactive_components.map((c) => c.name_fi).join(", ")}
                        {" — aktivoi varianteista yllä"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </AccentRailDisclosure>

      {operator && (
        <div className="absolute right-1 top-1 z-10 hidden group-hover:block">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              set_confirm_delete(true);
            }}
            className="rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title="Poista"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// One "Rakenne — aktiiviset komponentit" row — shared by the flat fallback list and every
// stream group so up/down semantics and subtype display stay identical between the two (#1157:
// only the ids fed to on_move_up/on_move_down differ — flat's full list vs. one stream's list).
function ActiveComponentRow({
  component,
  configurator,
  component_subtypes,
  set_subtype,
  can_move_up,
  can_move_down,
  on_move_up,
  on_move_down,
}: {
  component: KBComponent;
  configurator: boolean;
  component_subtypes: Record<string, string>;
  set_subtype: (component_id: string, subtype_id: string) => void;
  can_move_up: boolean;
  can_move_down: boolean;
  on_move_up: () => void;
  on_move_down: () => void;
}) {
  return (
    <div
      className={cn(moduleCard, "flex items-center gap-2 overflow-hidden p-2")}
    >
      <CardGlowOverlay />
      <span
        className={cn("relative h-2 w-2 shrink-0 rounded-full", cardToneDot.ok)}
        aria-hidden
      />
      <span className="relative flex-1 truncate text-xs font-medium">
        {component.name_fi}
      </span>
      {component.variants.length > 0 &&
        (configurator ? (
          <select
            value={component_subtypes[component.id] ?? ""}
            onChange={(e) => set_subtype(component.id, e.target.value)}
            className="relative rounded border border-border/40 bg-background px-1.5 py-1 text-[11px] outline-none focus:border-primary/60"
          >
            <option value="">Alatyyppi…</option>
            {component.variants.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name_fi}
              </option>
            ))}
          </select>
        ) : (
          component_subtypes[component.id] && (
            <span
              className={cn(
                "relative rounded-full border px-2 py-0.5 text-[10.5px]",
                cardTonePill.ok,
              )}
            >
              {component.variants.find(
                (v) => v.id === component_subtypes[component.id],
              )?.name_fi ?? component_subtypes[component.id]}
            </span>
          )
        ))}
      {configurator && (
        <div className="relative flex shrink-0 flex-col">
          <button
            type="button"
            disabled={!can_move_up}
            onClick={on_move_up}
            title="Siirrä ylös"
            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={!can_move_down}
            onClick={on_move_down}
            title="Siirrä alas"
            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
